import { startOfDay } from 'date-fns';
import * as Yup from 'yup';
import {
  FetchFavePassEnumsResponse,
  FetchMainCategoriesResponse,
} from '../../../services/FavePass';
import { ObjectShape } from 'yup/lib/object';
import {} from '../../../services/Company/useFetchCompanyDetails';
import { createObjectFromKey } from '../../../helpers/formHelpers';
import { FormFieldsRecord } from '../../../helpers/formHelpers/types';
import { FetchCompanyBankAccountsResponse } from '../../../services/Company/useFetchCompanyBankAccounts';
import { FetchCompanyOutletsResponse } from '../../../services/Company/useFetchCompanyOutlets';
import { FavePassDetailsViaMerchantConnectResponse } from '../../../services/FavePasses/useFetchFavePassViaMerchantConnect';
import { CreateUpdateFavePassViaMerchantConnectForm } from '../../../services/FavePasses/types';
import { useTenantContext } from '../../../contexts/TenantContext';
import { getValidity } from '../../../utils/utilFunctions';
import { validityTypes } from '../../../helpers/defaults';
import {
  mcEndDateValidation,
  mcStartDateValidation,
} from '../../../helpers/formHelpers/validationConstants';

// returns form fields, initial values and schema for fave pass form on merchant connect
export const useMerchantConnectFavePassFormBuilder = ({
  favePassEnums,
  favePassMainCategories,
  companyBankAccounts,
  companyOutlets,
  favePassDetails,
}: ApiResponse) => {
  const { tenantCurrency } = useTenantContext();
  const { days: validityDays, type: validityType } = getValidity(
    favePassDetails?.product_details.validity,
  );

  // form fields for fave pass
  const formFields: FormFieldsRecord = {
    // 1 row 1 field
    name: {
      label: 'Name',
      type: 'text',
      key: 'name',
      col: 24,
      value: favePassDetails?.product_details.name,
      schema: Yup.string()
        .max(120, 'Character limit is 120')
        .required('Required'),
      required: true,
    },
    // 1 row 1 field
    description: {
      label: 'Description',
      type: 'text',
      key: 'description',
      col: 24,
      value: favePassDetails?.product_details.description,
      schema: Yup.string()
        .max(120, 'Character limit is 120')
        .required('Required'),
      required: true,
    },
    // 1 row 3 fields
    total_sessions: {
      label: 'Total Sessions',
      type: 'number',
      key: 'total_sessions',
      col: 8,
      value: favePassDetails?.product_details.total_sessions,
      schema: Yup.number()
        .moreThan(2, 'Minimum sessions is 3')
        .typeError('Required')
        .required('Required'),
      required: true,
    },
    fee_percentage: {
      label: 'Fave Fee %',
      type: 'number',
      key: 'fee_percentage',
      col: 8,
      value: favePassDetails?.product_details.fee_percentage,
      schema: Yup.number()
        .min(0, 'Minimum is 0')
        .max(100, 'Maximum is 100')
        .typeError('Required')
        .required('Required'),
      required: true,
    },
    grabpay_later_fee_percentage: {
      label: 'PayLater by Grab Fee %',
      type: 'number',
      key: 'grabpay_later_fee_percentage',
      col: 8,
      value: favePassDetails?.product_details.grabpay_later_fee_percentage,
      schema: Yup.number()
        .min(0, 'Minimum is 0')
        .max(100, 'Maximum is 100')
        .nullable(),
    },
    // 1 row 2 fields
    validity: {
      label: 'Validity',
      type: 'number',
      key: 'validity',
      col: 12,
      value: validityDays,
      schema: Yup.number()
        .min(0, 'Minimum is 0')
        .typeError('Required')
        .required('Required'),
      required: true,
    },
    validity_type: {
      label: 'Validity Type',
      type: 'select',
      key: 'validity_type',
      options: validityTypes,
      col: 12,
      value: validityType,
      schema: Yup.string().required('Required'),
      required: true,
    },
    // 1 row 2 fields
    max_cap: {
      label: 'Max Cap',
      type: 'number',
      key: 'max_cap',
      col: 12,
      value: favePassDetails?.product_details.max_cap,
      schema: Yup.number().min(0, 'Minimum is 0').nullable(),
    },
    user_purchase_limit: {
      label: 'User Purchase Limit ( if empty, no limit )',
      type: 'number',
      key: 'user_purchase_limit',
      col: 12,
      value: favePassDetails?.product_details.user_purchase_limit,
      schema: Yup.number().min(0, 'Minimum is 0').nullable(),
    },
    // 1 row 2 fields
    main_category_id: {
      label: 'Main Category',
      type: 'select',
      key: 'main_category_id',
      options:
        favePassMainCategories?.main_categories?.map(
          ({ id, category_type }) => ({
            key: id,
            label: category_type,
            // send id as value to API
            value: id,
          }),
        ) || [],
      col: 12,
      value: favePassDetails?.product_details.main_category_id,
      schema: Yup.string().required('Required'),
      required: true,
    },
    reservation_requirement_type: {
      label: 'Reservation Requirement Type',
      type: 'select',
      key: 'reservation_requirement_type',
      options:
        favePassEnums?.reservation_requirement_type.map(({ key, label }) => ({
          key,
          label,
          // send key as value to API
          value: key,
        })) || [],
      col: 12,
      value: favePassDetails?.product_details.reservation_requirement_type,
      schema: Yup.string().required('Required'),
      required: true,
    },
    // 1 row 2 fields
    original_amount: {
      label: 'Original Amount',
      type: 'number',
      key: 'original_amount',
      col: 12,
      value:
        favePassDetails?.product_details.original_amount.amount || undefined,
      addonBefore:
        favePassDetails?.product_details.original_amount.currency ||
        tenantCurrency,
      precision: 2,
      schema: Yup.number()
        .min(0.01, 'Minimum is 0.01')
        .typeError('Required')
        .required('Required'),
      required: true,
    },
    discounted_amount: {
      label: 'Discounted Amount',
      type: 'number',
      key: 'discounted_amount',
      col: 12,
      value:
        favePassDetails?.product_details.discounted_amount.amount || undefined,
      addonBefore:
        favePassDetails?.product_details.discounted_amount.currency ||
        tenantCurrency,
      precision: 2,
      schema: Yup.number()
        .min(0.01, 'Minimum is 0.01')
        .typeError('Required')
        .required('Required')
        .when(
          'original_amount',
          (originalAmount: number, schema: Yup.NumberSchema) => {
            if (originalAmount)
              return schema.test(
                'is-less-than-original',
                'Discounted amount must be strictly less than the original amount',
                discountedAmount => {
                  return discountedAmount !== undefined &&
                    originalAmount !== undefined
                    ? discountedAmount < originalAmount
                    : true;
                },
              );
            return schema;
          },
        ),
      required: true,
    },
    // 1 row 2 fields
    start_date: {
      label: 'Start Date',
      type: 'datepicker',
      key: 'start_date',
      col: 12,
      value: favePassDetails?.product_details.start_date || undefined,
      disabledDate: currentDate =>
        // cannot select past dates
        currentDate < startOfDay(new Date()),
      schema: mcStartDateValidation('start_date'),
      required: true,
    },
    end_date: {
      label: 'End Date',
      type: 'datepicker',
      key: 'end_date',
      col: 12,
      value: favePassDetails?.product_details.end_date || undefined,
      disabledDate: currentDate =>
        // cannot select past dates
        currentDate < startOfDay(new Date()),
      schema: mcEndDateValidation('start_date'),
      required: true,
    },
    // 1 row 1 field
    finance_account_id: {
      label: 'Bank Account',
      type: 'select',
      key: 'finance_account_id',
      options:
        companyBankAccounts?.finance_accounts.map(
          ({ id, bank_account_name, bank_account_number }) => ({
            key: id,
            label: `${bank_account_name} - ${bank_account_number}`,
            // send id as value to API
            value: id,
          }),
        ) || [],
      col: 24,
      value: favePassDetails?.product_details.finance_account_id || undefined,
      schema: Yup.string().required('Required'),
      required: true,
    },
    // 1 row 1 field
    outlet_ids: {
      label: 'Outlet(s)',
      type: 'select',
      key: 'outlet_ids',
      options:
        // show only active outlets
        companyOutlets?.outlets
          .filter(outlet => outlet.status === 'active')
          .map(item => ({
            key: item.id,
            label: item.name,
            value: item.id,
          })) || [],
      mode: 'multiple',
      col: 24,
      value: favePassDetails?.product_details.outlet_ids || undefined,
      schema: Yup.array()
        .of(Yup.string())
        .min(1, 'Must contain at least one outlet.')
        .required('Must contain at least one outlet.'),
      required: true,
    },
    // 1 row 1 field
    what_you_get: {
      label: 'What You Get',
      type: 'editor',
      key: 'what_you_get',
      value: favePassDetails?.product_details.what_you_get,
      schema: Yup.string().required('Required'),
      required: true,
    },
    // 1 row 1 field
    fine_print: {
      label: 'Fine Print',
      type: 'editor',
      key: 'fine_print',
      value: favePassDetails?.product_details.fine_print,
      schema: Yup.string().nullable(),
    },
    // 1 row 1 field
    cancellation_policy: {
      label:
        'Cancellation Policy ( if empty, we will follow default cancellation policy. )',
      type: 'textarea',
      key: 'cancellation_policy',
      value: favePassDetails?.product_details.cancellation_policy,
      schema: Yup.string().nullable(),
      rows: 5,
    },
    // 1 row 1 field
    image_url: {
      label: 'Image (327px x 176px)',
      type: 'image',
      key: 'image_url',
      maxCount: 1,
      value: favePassDetails?.product_details.image_url
        ? [
            {
              url: favePassDetails?.product_details.image_url,
              thumbUrl: favePassDetails?.product_details.image_url,
              uid: 'default-uid',
              name: 'default-file',
            },
          ]
        : [],
      schema: Yup.array().of(
        Yup.mixed().test(
          'fileSize',
          'The file size is too large. Maximum allowed size is 10MB.',
          value => {
            const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

            // in edit mode, value.size is not available
            if (!value.size) return true;
            // Ensure a file is provided and check the size
            return value.size <= MAX_FILE_SIZE;
          },
        ),
      ),
    },
  };

  // create initial values for form
  const initialValues = createObjectFromKey(
    formFields,
    'value' as keyof FormFieldsRecord[keyof FormFieldsRecord],
  ) as CreateUpdateFavePassViaMerchantConnectForm;
  // create schema for form
  const formSchema = Yup.object().shape(
    createObjectFromKey(formFields, 'schema') as ObjectShape,
  );

  return { formFields, initialValues, formSchema };
};

type ApiResponse = {
  favePassEnums?: FetchFavePassEnumsResponse;
  favePassMainCategories?: FetchMainCategoriesResponse;
  companyBankAccounts?: FetchCompanyBankAccountsResponse;
  companyOutlets?: FetchCompanyOutletsResponse;
  favePassDetails?: FavePassDetailsViaMerchantConnectResponse;
};
